/**
 * Inspiration upload path for `grid plug <target>` per CLI UX spec v17
 * §7.2 and the Inspiration API v2.0 contract (`docs/contracts/inspiration-api.md`).
 *
 * The runtime path lives in `commands/plug.ts`; this module owns the branch
 * that fires when `target` is a local file or directory. URL-based ingestion
 * (`https://...`) and `--from-claude` are still deferred — the runtime path's
 * dispatcher surfaces a clean "not yet" error before reaching here.
 *
 * The endpoint is `POST /api/v2/inspirations` (v2.0 frozen contract). The
 * legacy `POST /api/v1/drops` surface remains live for the 90-day
 * deprecation window but is NOT called from this code path under any
 * circumstance.
 */

import { existsSync, statSync, readFileSync } from 'node:fs';
import { basename, extname, resolve } from 'node:path';

import { CloudGridError } from '../utils.js';
import type { IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { getActiveOrg } from '../active-org.js';
import { zipDirectory } from '../services/zip.js';
import { copyToClipboard as defaultCopyToClipboard } from '../services/clipboard.js';
import type { PlugOpts, PlugDeps } from './plug.js';

// ─────────────────────────────────────────────────────────────────────────────
// Source-type detection
// ─────────────────────────────────────────────────────────────────────────────

type InspirationSourceType =
  | 'html'
  | 'react_single_file'
  | 'markdown'
  | 'pdf'
  | 'pptx'
  | 'docx'
  | 'xlsx'
  | 'image'
  // Backlog 116 Tier 1.
  | 'mermaid'
  | 'code'
  | 'json'
  // Backlog 120 Tier 2.
  | 'video'
  | 'audio'
  | 'text'
  | 'jupyter'
  | 'csv';

interface SourceTypeMatch {
  source_type: InspirationSourceType;
  /** Human-friendly note appended to the resolution log. */
  note?: string;
}

/**
 * Map a file extension (lowercase, with leading dot) to the v2 contract's
 * `source_type` enum. Returns `null` when the extension is not supported by
 * this CLI version.
 *
 * The extension → source_type table is the shared Drop Zone v2 detection
 * engine's `INSPIRATION_SOURCE_TYPES` (spec #761 Phase 1) — the platform's
 * single source of truth, so the CLI and the browser drop surface never drift
 * on what counts as an inspiration. `.zip` is an archive rather than a leaf
 * file type, so it is special-cased here before the shared lookup.
 */
function detectSourceType(
  ext: string,
  sourceTypes: Readonly<Record<string, string>>,
): SourceTypeMatch | null {
  if (ext === '.zip') {
    return {
      source_type: 'html',
      note: 'zip archives must contain index.html at the root',
    };
  }
  const sourceType = sourceTypes[ext];
  return sourceType ? { source_type: sourceType as InspirationSourceType } : null;
}

const SUPPORTED_EXTS =
  '.html, .htm, .md, .pdf, .pptx, .docx, .xlsx, .png, .jpg, .jpeg, .svg, .webp, .zip, ' +
  '.jsx, .tsx, .mmd, .json, .ts, .js, .py, .go, .rs, .sql, .yaml, .yml, .toml, ' +
  '.mp4, .webm, .mov, .ogv, .mp3, .wav, .m4a, .ogg, .txt, .log, .ipynb, .csv, .tsv';

// ─────────────────────────────────────────────────────────────────────────────
// Slug derivation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive a contract-shaped base slug from a filename. The server
 * (`docs/contracts/inspiration-api.md` §4) appends a 4-char hex suffix to
 * whatever the client submits and re-validates against `^[a-z][a-z0-9-]*$`,
 * 3–40 chars. We pre-clean here so common filenames produce a slug the
 * server accepts on the first try.
 */
function deriveBaseSlug(filename: string): string {
  const stem = basename(filename).replace(/\.[^.]+$/, '');
  let s = stem
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!/^[a-z]/.test(s)) s = `x-${s}`.replace(/-+$/g, '');
  if (s.length > 40) s = s.substring(0, 40).replace(/-+$/g, '');
  return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// Target resolution
// ─────────────────────────────────────────────────────────────────────────────

interface ResolvedTarget {
  /** Path on disk to the file that becomes the upload payload. */
  filePath: string;
  /** Original filename (or directory basename) — used for slug derivation. */
  originalName: string;
  /** Lowercased extension of the upload (e.g. `.zip`). */
  extension: string;
  /** Cleanup hook for any temp files created (e.g. zipping a directory). */
  cleanup?: () => void;
  /** True when the target was a directory and we zipped it. */
  zippedFromDirectory?: boolean;
}

function isUrl(target: string): boolean {
  return /^https?:\/\//i.test(target);
}

function resolveTarget(target: string, cwd: string): ResolvedTarget {
  const abs = resolve(cwd, target);
  if (!existsSync(abs)) {
    throw new CloudGridError(
      `Path not found: ${target}\n` +
        'Pass a path to a local file or directory, or run `grid plug --help` for the full list of options.',
    );
  }
  const stat = statSync(abs);
  if (stat.isDirectory()) {
    const dirName = basename(abs);
    const { path: zipPath, cleanup } = zipDirectory(abs);
    return {
      filePath: zipPath,
      originalName: `${dirName}.zip`,
      extension: '.zip',
      cleanup,
      zippedFromDirectory: true,
    };
  }
  if (stat.isFile()) {
    const name = basename(abs);
    return {
      filePath: abs,
      originalName: name,
      extension: extname(name).toLowerCase(),
    };
  }
  throw new CloudGridError(
    `Target is neither a file nor a directory: ${target}`,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Flag validation
// ─────────────────────────────────────────────────────────────────────────────

const VALID_SCOPES = new Set(['personal', 'space', 'org']);
// BL513 — `link-anonymous` / `link-indexed` are the first-class link modes;
// `link` stays accepted as a deprecated alias (maps to `link-anonymous`).
const VALID_VISIBILITIES = new Set([
  'private',
  'space',
  'org',
  'link',
  'link-anonymous',
  'link-indexed',
]);

interface ValidatedFlags {
  scope: 'personal' | 'space' | 'org';
  visibility: 'private' | 'space' | 'org' | 'link' | 'link-anonymous' | 'link-indexed';
  spaceId?: string;
  expiresInDays: number | null; // null = pinned
  pinReason?: string;
  baseName: string;
  displayName?: string;
  inspiredBy: string[];
}

function validateFlags(opts: PlugOpts, defaultBaseName: string): ValidatedFlags {
  const scope = (opts.scope ?? 'personal').toLowerCase();
  if (!VALID_SCOPES.has(scope)) {
    throw new CloudGridError(
      `Unknown --scope '${opts.scope}'. Valid: personal, space, org.`,
    );
  }
  // BL507 / strategy §7.6 — `grid plug` creates an inspiration in the
  // caller's (authed) org; for the default `team` org the inspiration default is
  // `'org'` (the serialized `org+spaces=[general]`), which is also the only
  // scope-universal value (contract §4 admits `org` with personal/space/org
  // scope). Per-org-type defaults (a `personal` org → `private`) are BL509. The
  // owner shares wider explicitly via `--visibility`, which now also accepts the
  // first-class `link-anonymous` / `link-indexed` modes (BL513).
  const visibility = (opts.visibility ?? 'org').toLowerCase();
  if (!VALID_VISIBILITIES.has(visibility)) {
    throw new CloudGridError(
      `Unknown --visibility '${opts.visibility}'. Valid: private, space, org, link, link-anonymous, link-indexed.`,
    );
  }

  if (scope === 'space' && !opts.space) {
    throw new CloudGridError(
      '--scope space requires --space <slug>.',
    );
  }

  let expiresInDays: number | null = 30;
  if (opts.expires !== undefined) {
    const v = String(opts.expires).trim();
    if (v === 'never') {
      expiresInDays = null;
    } else {
      const n = parseInt(v, 10);
      if (!Number.isFinite(n) || n < 1 || n > 365) {
        throw new CloudGridError(
          `--expires must be an integer 1-365 or "never". Got: ${opts.expires}`,
        );
      }
      expiresInDays = n;
    }
  }

  if (expiresInDays === null && !opts.pinReason) {
    throw new CloudGridError(
      '--expires never requires --pin-reason <text>. Pinned inspirations need a written reason so they can be reviewed later.',
    );
  }

  const baseName = (opts.name ?? defaultBaseName).trim();
  if (baseName.length === 0) {
    throw new CloudGridError(
      'Could not derive a slug from the filename. Pass --name <slug> with 3-40 characters: lowercase letters, digits, and dashes; must start with a letter.',
    );
  }

  return {
    scope: scope as ValidatedFlags['scope'],
    visibility: visibility as ValidatedFlags['visibility'],
    spaceId: opts.space,
    expiresInDays,
    pinReason: opts.pinReason,
    baseName,
    displayName: opts.displayName,
    inspiredBy: opts.inspiredBy ?? [],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// MIME helper
// ─────────────────────────────────────────────────────────────────────────────

function mimeFor(ext: string): string {
  switch (ext) {
    case '.html':
    case '.htm':
      return 'text/html';
    case '.md':
      return 'text/markdown';
    case '.pdf':
      return 'application/pdf';
    case '.pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case '.xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case '.png':
      return 'image/png';
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.svg':
      return 'image/svg+xml';
    case '.webp':
      return 'image/webp';
    case '.zip':
      return 'application/zip';
    case '.json':
      return 'application/json';
    case '.mmd':
      return 'text/plain';
    case '.ts':
    case '.tsx':
    case '.js':
    case '.jsx':
    case '.py':
    case '.go':
    case '.rs':
    case '.sql':
    case '.yaml':
    case '.yml':
    case '.toml':
      return 'text/plain';
    // Backlog 120 Tier 2.
    case '.mp4':
      return 'video/mp4';
    case '.webm':
      return 'video/webm';
    case '.mov':
      return 'video/quicktime';
    case '.ogv':
      return 'video/ogg';
    case '.mp3':
      return 'audio/mpeg';
    case '.wav':
      return 'audio/wav';
    case '.m4a':
      return 'audio/mp4';
    case '.ogg':
      return 'audio/ogg';
    case '.txt':
    case '.log':
      return 'text/plain';
    case '.ipynb':
      return 'application/json';
    case '.csv':
      return 'text/csv';
    case '.tsv':
      return 'text/tab-separated-values';
    default:
      return 'application/octet-stream';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Server response shape (subset we surface to the user)
// ─────────────────────────────────────────────────────────────────────────────

interface InspirationCreateResponse {
  entity_id?: string;
  name?: string;
  display_name?: string;
  url?: string;
  fallback_urls?: string[];
  scope?: string;
  visibility?: string;
  expires_at?: string | null;
  pinned?: boolean;
  source_type?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public runner
// ─────────────────────────────────────────────────────────────────────────────

export async function runInspirationPlug(
  client: IV2Client,
  target: string,
  opts: PlugOpts,
  deps: PlugDeps,
): Promise<void> {
  const cwd = deps.cwd ?? process.cwd();
  const writer = deps.writer ?? ((s: string) => process.stdout.write(s));

  if (isUrl(target)) {
    throw new CloudGridError(
      'URL-based inspiration upload is not yet implemented. Save the artifact locally and pass the path: grid plug ./<file>.',
    );
  }

  if (opts.fromClaude) {
    throw new CloudGridError(
      '--from-claude is not yet implemented. Save the artifact locally and pass the path: grid plug ./<file>.',
    );
  }

  // Resolve the target shape first so we know whether to zip and what slug
  // to derive from. Surface the bundling note before the zip runs so the
  // user sees what we're packaging up.
  const stat = existsSync(resolve(cwd, target)) ? statSync(resolve(cwd, target)) : null;
  if (stat?.isDirectory()) {
    if (!opts.json) {
      writer(`Bundling ${target} as a zip (index.html at root expected for html source).\n`);
    }
  }

  const resolved = resolveTarget(target, cwd);

  try {
    // Spec #761 Phase 1 — the inspiration extension set is the platform's
    // single source of truth, shared with the Drop Zone v2 detector so the CLI
    // and the browser never drift. Loaded via dynamic import, matching how the
    // runtime path reaches `packages/shared` (see commands/plug.ts).
    const { INSPIRATION_SOURCE_TYPES } = await import(
      '../../../shared/src/drop-detection/heuristics/inspiration.js'
    );
    const sourceMatch = detectSourceType(resolved.extension, INSPIRATION_SOURCE_TYPES);
    if (!sourceMatch) {
      throw new CloudGridError(
        `Unsupported file type: ${resolved.extension || '(no extension)'}\n` +
          `Supported: ${SUPPORTED_EXTS}.`,
      );
    }

    // Derive default slug from the original filename (or zipped dir name).
    const defaultBaseName = deriveBaseSlug(resolved.originalName);
    const flags = validateFlags(opts, defaultBaseName);

    // Server-authoritative active-org resolution (same pattern the runtime
    // path uses). `--org <slug>` (backlog 308/464) takes precedence over the
    // persisted active org for this call; absent it, the persisted active org
    // wins. Throws CloudGridError with §23-voice wording when no active org is
    // set or the slug is stale.
    const activeOrg = await getActiveOrg(client, { override: opts.org });

    // Backlog 464 — rebind the client to the *resolved* org so the inspiration
    // POST (and the outlet it returns) lands in the effective `--org`, not the
    // slug the client was first built with. Without this the runtime path
    // honoured `--org` (it rebinds in plug.ts) while the inspiration path
    // silently dropped into the persisted active org — a wrong-destination bug.
    const orgClient = client.withOrgSlug(activeOrg.slug);

    if (!opts.json) {
      const pretty = resolved.zippedFromDirectory
        ? `${basename(target)} (zipped)`
        : basename(target);
      writer(`Plugging ${flags.baseName} into ${activeOrg.slug}-hub...\n`);
      writer(`  Source: ${pretty}\n`);
      writer(`  Type:   inspiration (${sourceMatch.source_type})\n`);
      writer(`  Scope:  ${flags.scope}\n`);
      writer(`  Visibility: ${flags.visibility}\n`);
      const expiresLine =
        flags.expiresInDays === null
          ? 'pinned'
          : `${flags.expiresInDays} days`;
      writer(`  Expires: ${expiresLine}\n`);
      if (sourceMatch.note) {
        writer(`  Note: ${sourceMatch.note}\n`);
      }
    }

    // Build the multipart body. Reading the file into memory caps usable
    // upload size at process memory; the contract caps payloads at 50 MB
    // anyway (§8 rate limits) so the simple read is fine in v0.8.
    const fileBuffer = readFileSync(resolved.filePath);
    // Copy into a fresh ArrayBuffer because lib types want strict
    // ArrayBuffer-typed `BlobPart`, not `Buffer<ArrayBufferLike>`.
    const fileBytes = new Uint8Array(fileBuffer.byteLength);
    fileBytes.set(fileBuffer);

    const form = new FormData();
    form.append(
      'file',
      new Blob([fileBytes.buffer], { type: mimeFor(resolved.extension) }),
      resolved.originalName,
    );
    form.append('name', flags.baseName);
    form.append('scope', flags.scope);
    form.append('visibility', flags.visibility);
    form.append('source_type', sourceMatch.source_type);
    form.append('uploaded_via', 'cli');

    if (flags.spaceId) form.append('space_id', flags.spaceId);
    if (flags.displayName) form.append('display_name', flags.displayName);
    if (flags.expiresInDays === null) {
      // Multipart fields are always strings on the wire. The v2 route
      // (`packages/api/src/routes/v2/inspirations.ts`) accepts the literal
      // string 'null' and treats it as pinned. Field-absence would default
      // to 30 days — so we can't omit when pinned, we must send the magic
      // string the route is wired to recognise.
      form.append('expires_in_days', 'null');
      if (flags.pinReason) form.append('pin_reason', flags.pinReason);
    } else {
      form.append('expires_in_days', String(flags.expiresInDays));
    }
    for (const id of flags.inspiredBy) {
      form.append('inspired_by', id);
    }

    let result: InspirationCreateResponse;
    try {
      result = await orgClient.postMultipart<InspirationCreateResponse>(
        '/api/v2/inspirations',
        form,
      );
    } catch (err) {
      if (err instanceof V2ApiError) {
        // Surface the server's message verbatim — it's already shaped per
        // contract §6 (VALIDATION_FAILED, SCOPE_INVALID, SLUG_TAKEN, etc.)
        // and §23 voice: plain, specific, no marketing language.
        throw new CloudGridError(err.message);
      }
      throw err;
    }

    const outletUrl = result.url ?? '';

    // Best-effort clipboard write. Default ON; --no-clipboard opts out. The
    // helper never throws — a missing pbcopy/xclip/clip.exe resolves false
    // and we move on. We do NOT surface success/failure to the user; the
    // outlet URL is already printed (human mode) or in the JSON payload.
    if (outletUrl && !opts.noClipboard) {
      const copy = deps.copyToClipboard ?? defaultCopyToClipboard;
      await copy(outletUrl);
    }

    if (opts.json) {
      const payload = {
        status: 'success',
        entity_id: result.entity_id ?? '',
        name: result.name ?? flags.baseName,
        outlet: outletUrl,
        source_type: sourceMatch.source_type,
        scope: result.scope ?? flags.scope,
        visibility: result.visibility ?? flags.visibility,
      };
      process.stdout.write(JSON.stringify(payload) + '\n');
      return;
    }

    writer('  Live.\n');
    if (outletUrl) {
      writer(`  Outlet: ${outletUrl}\n`);
    }
  } finally {
    resolved.cleanup?.();
  }
}
